'use client';

import { CityPageQueryQuery } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { BranchScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/branch-schedule-button.component';
import { getServiceChannelFlags } from '@/app/(main)/(pages)/_utils/business-model.util';
import { MedprevOnlineScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/medprev-online-schedule-button.component';
import { SearchSectionCtaButtons } from '@/app/(main)/(pages)/_components/search-section/common/search-section-cta-buttons.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { cn } from '@/utils/cn-tw-merge';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface HealthCareSectionProps {
  cityConfig?: CityPageQueryQuery;
  isBranchPage?: boolean;
  branchWhatsapp?: string;
}

export const HealthCareSection = ({
  cityConfig,
  isBranchPage = false,
  branchWhatsapp = '',
}: HealthCareSectionProps) => {
  const {
    hasOneCta,
    hasBranches,
    onlyTelemedicineServiceChannel,
    branchesLength,
  } = getServiceChannelFlags(cityConfig);
  const analytics = getAnalyticsPlugin();

  const shouldAddTopPadding
    = !isBranchPage
      && !onlyTelemedicineServiceChannel
      && (!hasBranches || !branchesLength);

  const hasSingleButtonLayout = hasOneCta || !cityConfig;

  const renderCtaButtons = () => {
    if (isBranchPage) {
      return (
        <BranchScheduleButton
          color="success"
          variant="default"
          branchesLength={1}
          whatsapp={branchWhatsapp}
          onClick={() => {
            analytics.track(AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_BRANCH, {
              secao_origem: 'rodape',
              nome_secao: 'cta_final',
            });
          }}
        />
      );
    }

    if (cityConfig) {
      return (
        <SearchSectionCtaButtons
          cityConfig={cityConfig}
          isAfterBranchesSection
          medprevScheduleButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_BY_WEBSITE}
          assistedPurchaseButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_SUPPORT}
          telemedicineButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_TELEHEALTH}
          from="rodape"
          sessionName="cta_final"
        />
      );
    }

    return (
      <MedprevOnlineScheduleButton
        titleButton="Agendar pelo site"
        data-analytics-event={AnalyticsEnum.CLICK_SCHEDULE_NOW_HOME_PAGE}
        from="rodape"
        sessionName="cta_final"
      />
    );
  };

  return (
    <section
      data-testid={DataTestIdEnum.CITY_HEALTH_CARE_SECTION}
      className="bg-[linear-gradient(281deg,_#095E3A_33.66%,_#0D9259_66.34%)] xl:bg-gray-50 xl:[background-image:none]"
    >
      <ContentContainer
        className={cn(
          shouldAddTopPadding ? 'xl:pt-10' : 'xl:pt-0',
          'py-10 sm:py-16 xl:pb-10',
        )}
      >
        <div className="flex flex-col items-center xl:rounded-2xl xl:py-10 xl:bg-[linear-gradient(281deg,_#095E3A_33.66%,_#0D9259_66.34%)]">
          <Typography
            variant={TypographyVariant.LARGE_SUBTITLE}
            className="mb-1 text-center font-bold text-white"
          >
            Está pronto para cuidar da sua saúde hoje mesmo?
          </Typography>

          <Typography
            variant={TypographyVariant.MEDIUM_SUBTITLE}
            className="mb-6 text-center text-white"
          >
            Escolha sua especialidade ou exame, agende e seja atendido sem burocracia.
          </Typography>

          <div
            className={cn(
              hasSingleButtonLayout
                ? 'sm:w-[18.75rem]'
                : 'sm:w-[18.75rem] xl:w-[38.5rem]',
              'flex w-full flex-col justify-center gap-4 xl:flex-row',
            )}
          >
            {renderCtaButtons()}
          </div>
        </div>
      </ContentContainer>
    </section>
  );
};
